'use client';

import {
  animate,
  HTMLMotionProps,
  motion,
  MotionValue,
  noop,
  resize,
  useAnimationFrame,
  useInView,
  useIsomorphicLayoutEffect,
  useMotionValue,
  usePageInView,
  useReducedMotion,
  useTransform,
  wrap,
} from 'motion/react';
import { useMemo, useRef, useState } from 'react';
import { ItemSize, TickerItem } from './TickerItem';
import { ItemPosition } from './types';
import { useFocusNavigation } from './use-focus-navigation';
import { calcNumClones } from './utils/calc-num-clones';
import { calcTotalItemLength } from './utils/calc-total-item-length';

/**
 * Props for the Ticker component.
 */
export interface TickerProps {
  /**
   * An array of React nodes to be rendered as ticker items.
   */
  items: React.ReactNode[];

  /**
   * The axis along which the ticker scrolls. Defaults to "x".
   */
  axis?: 'x' | 'y';

  /**
   * The velocity of the ticker scroll in pixels per second. Defaults to 50.
   */
  velocity?: number; // pixels per second

  /**
   * Factor by which the velocity is multiplied when the ticker is hovered. Defaults to 1 (no change).
   */
  hoverFactor?: number; // 0-1

  /**
   * The gap between ticker items in pixels. Defaults to 10.
   */
  gap?: number; // gap between items

  /**
   * Alignment of items within the ticker. Defaults to "center".
   */
  align?: 'start' | 'center' | 'end' | 'stretch';

  /**
   * An optional MotionValue to control the ticker's offset externally.
   */
  offset?: MotionValue<number>;

  /**
   * The size of the ticker items. Defaults to "auto".
   */
  itemSize?: ItemSize;

  /**
   * Show items that overflow the container.
   */
  overflow?: boolean;
}

interface TickerState {
  visibleLength: number;
  inset: number;
  totalItemLength: number;
  itemPositions: ItemPosition[];
}

const alignAlias = {
  start: 'flex-start',
  end: 'flex-end',
} as const;

/**
 * A performant, accessible, and infinitely scrolling ticker component.
 *
 * @param items - An array of React nodes to be rendered as ticker items.
 * @param velocity - The velocity of the ticker scroll in pixels per second. Defaults to 50.
 * @param hoverFactor - Factor by which the velocity is multiplied when the ticker is hovered. Defaults to 1 (no change).
 * @param gap - The gap between ticker items in pixels. Defaults to 10.
 * @param axis - The axis along which the ticker scrolls. Defaults to "x".
 * @param align - Alignment of items within the ticker. Defaults to "center".
 * @param offset - An optional MotionValue to control the ticker's offset externally.
 * Defaults to false.
 * @param props - HTML attributes to pass to the ticker container div.
 * @returns A Ticker React component.
 */
export function Ticker({
  items,
  velocity = 50,
  hoverFactor = 1,
  gap = 10,
  axis = 'x',
  align = 'center',
  offset,
  itemSize = 'auto',
  overflow = false,
  ...props
}: HTMLMotionProps<'div'> & TickerProps) {
  const [state, setState] = useState<TickerState>({
    visibleLength: 0,
    inset: 0,
    totalItemLength: 0,
    itemPositions: [],
  });

  const alignItems = alignAlias[align as keyof typeof alignAlias] || align;

  // SCS: isStatic was causing issues because it conditionally calls hooks
  //   if (isStatic) {
  //     const renderedOffset = useMotionValue(0);
  //     return (
  //       <ListView
  //         containerProps={props}
  //         gap={gap}
  //         axis={axis}
  //         alignItems={alignItems}
  //         renderedOffset={renderedOffset}
  //         items={items}
  //         itemSize={itemSize}
  //         state={state}
  //         overflow={overflow}
  //         isStatic
  //       />
  //     );
  //   }

  const [hasFocus, setHasFocus] = useState(false);

  const velocityFactor = useMotionValue(1);
  const defaultOffset = useMotionValue(0);
  offset ??= defaultOffset;

  const wrappedOffset = useTransform(() =>
    wrap(-state.totalItemLength - gap - state.inset, -state.inset, offset.get()),
  );
  const focusOffset = useMotionValue(0);

  const renderedOffset = hasFocus ? focusOffset : wrappedOffset;

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const isInViewport = useInView(containerRef, { margin: '100px' });
  const isPageInView = usePageInView();
  const isInView = isInViewport && isPageInView;
  const isReducedMotion = useReducedMotion();

  const updateMeasurements = () => {
    if (!containerRef.current || !listRef.current) return;

    const viewportLengthProp = axis === 'x' ? 'innerWidth' : 'innerHeight';
    const lengthProp = axis === 'x' ? 'offsetWidth' : 'offsetHeight';
    const insetProp = axis === 'x' ? 'offsetLeft' : 'offsetTop';
    const paddingStartProp = axis === 'x' ? 'paddingLeft' : 'paddingTop';

    const container = containerRef.current;
    const list = listRef.current;
    const allItems = list.querySelectorAll('.ticker-item');

    if (!allItems.length) return;

    let haveItemSizesChanged = false;
    const itemPositions = [];
    for (let i = 0; i < allItems.length; i++) {
      const item = allItems[i] as HTMLElement;
      itemPositions.push({
        start: item[insetProp],
        end: item[insetProp] + item[lengthProp],
      });

      if (
        !state.itemPositions[i] ||
        itemPositions[i].start !== state.itemPositions[i].start ||
        itemPositions[i].end !== state.itemPositions[i].end
      ) {
        haveItemSizesChanged = true;
      }
    }

    /**
     * Total visible length of the ticker
     */
    const visibleLength = overflow
      ? window[viewportLengthProp]
      : /**
         * Cap to viewport size to prevent infinite or wasteful cloning in the event that the
         * container width is reactive to the number of children rendered within it.
         */
        Math.min(container[lengthProp], window[viewportLengthProp]);

    const totalItemLength = calcTotalItemLength(itemPositions);

    const computedContainerStyle = window.getComputedStyle(container);
    const containerPaddingStart = parseInt(computedContainerStyle[paddingStartProp] ?? 0);

    const inset = overflow ? getCumulativeOffset(allItems[0] as HTMLElement, axis) : containerPaddingStart;

    if (
      visibleLength !== state.visibleLength ||
      totalItemLength !== state.totalItemLength ||
      inset !== state.inset ||
      haveItemSizesChanged
    ) {
      setState({
        visibleLength,
        itemPositions,
        totalItemLength,
        inset,
      });
    }
  };

  useIsomorphicLayoutEffect(() => {
    if (!isInView || !containerRef.current) return;
    updateMeasurements();

    const trackViewport = overflow ? resize(updateMeasurements) : undefined;

    const trackContainer = resize(containerRef.current, updateMeasurements);

    return () => {
      trackViewport?.();
      trackContainer();
    };
  }, [items, isInView, overflow]);

  const isMeasured = state.totalItemLength > 0;

  useAnimationFrame(
    isMeasured && isInView && offset === defaultOffset && !isReducedMotion
      ? (_, delta) => {
          const frameOffset = (delta / 1000) * (velocity * velocityFactor.get());
          offset.set(offset.get() - frameOffset);
        }
      : noop,
  );

  const cloneCount = useMemo(() => {
    if (!isMeasured || !state.visibleLength) return 0;

    return calcNumClones(state.visibleLength, state.itemPositions, gap);
  }, [isMeasured, state]);

  const totalListSize = state.totalItemLength === 0 ? 0 : (state.totalItemLength + gap) * (cloneCount + 1);

  const clonedItems: React.ReactNode[] = [];
  for (let i = 0; i < cloneCount; i++) {
    items.forEach((item, itemIndex) => {
      const originalBounds = state.itemPositions[itemIndex];
      const cloneOffset = (state.totalItemLength + gap) * (i + 1);
      const cloneBounds = originalBounds
        ? {
            start: originalBounds.start + cloneOffset,
            end: originalBounds.end + cloneOffset,
          }
        : defaultBounds;

      clonedItems.push(
        <TickerItem
          key={`clone-${i}-${itemIndex}`}
          offset={renderedOffset}
          axis={axis}
          listSize={totalListSize}
          cloneIndex={itemIndex}
          bounds={cloneBounds}
          inset={state.inset}
          alignItems={alignItems}
          itemSize={itemSize}
        >
          {item}
        </TickerItem>,
      );
    });
  }

  useFocusNavigation(containerRef, axis, focusOffset, offset, setHasFocus);

  return (
    <ListView
      containerProps={props}
      containerRef={containerRef}
      listRef={listRef}
      gap={gap}
      axis={axis}
      alignItems={alignItems}
      isMeasured={isMeasured}
      isInView={isInView}
      renderedOffset={renderedOffset}
      items={items}
      itemSize={itemSize}
      clonedItems={clonedItems}
      onPointerEnter={() => {
        animate(velocityFactor, hoverFactor);
      }}
      onPointerLeave={() => {
        animate(velocityFactor, 1);
      }}
      totalListSize={totalListSize}
      state={state}
      overflow={overflow}
    />
  );
}

function ListView({
  containerProps,
  containerRef,
  listRef,
  gap,
  axis,
  alignItems,
  isMeasured,
  isInView,
  isStatic,
  items,
  clonedItems,
  renderedOffset,
  onPointerEnter,
  onPointerLeave,
  totalListSize,
  itemSize,
  overflow,
  state,
}: {
  containerProps: HTMLMotionProps<'div'>;
  containerRef?: React.RefObject<HTMLDivElement>;
  listRef?: React.RefObject<HTMLUListElement>;
  gap: number;
  axis: 'x' | 'y';
  alignItems: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  renderedOffset: MotionValue<number>;
  isMeasured?: boolean;
  isInView?: boolean;
  isStatic?: boolean;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  items: React.ReactNode[];
  clonedItems?: React.ReactNode[];
  totalListSize?: number;
  itemSize: ItemSize;
  overflow: boolean;
  state: TickerState;
}) {
  return (
    <motion.div
      {...containerProps}
      ref={containerRef}
      style={{
        ...containerStyle,
        ...containerProps.style,
        overflow: overflow ? 'visible' : 'hidden',
      }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <motion.ul
        ref={listRef}
        style={{
          ...listStyle,
          flexDirection: axis === 'x' ? 'row' : 'column',
          gap: `${gap}px`,
          x: axis === 'x' ? renderedOffset : 0,
          y: axis === 'y' ? renderedOffset : 0,
          opacity: isMeasured || isStatic ? 1 : 0,
          alignItems,
          willChange: isMeasured && isInView ? 'transform' : undefined,
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          maxWidth: '100%',
        }}
      >
        {items.map((item, index) => (
          <TickerItem
            key={'original-' + index}
            axis={axis}
            offset={renderedOffset}
            listSize={totalListSize}
            itemIndex={index}
            bounds={state.itemPositions[index] ?? defaultBounds}
            inset={state.inset}
            alignItems={alignItems}
            itemSize={itemSize}
          >
            {item}
          </TickerItem>
        ))}
        {clonedItems || null}
      </motion.ul>
    </motion.div>
  );
}

const defaultBounds = { start: 0, end: 0 };

const containerStyle: React.CSSProperties = {
  display: 'flex',
  position: 'relative',
  overflow: 'hidden',
};

const listStyle: React.CSSProperties = {
  display: 'flex',
  position: 'relative',
  willChange: 'transform',
  listStyleType: 'none',
  padding: 0,
  margin: 0,
  justifyContent: 'flex-start',
};

function getCumulativeOffset(element: HTMLElement, axis: 'x' | 'y') {
  let offset = 0;
  let el: HTMLElement | null = element;
  while (el) {
    offset += axis === 'x' ? el.offsetLeft : el.offsetTop;
    el = el.offsetParent as HTMLElement;
  }
  return offset;
}
